import axios from "axios";
import { AUTH_ENDPOINTS } from "../config/apiConfig";
import { createAuthAxios, handleApiError } from "../utils/apiUtils";

/**
 * Service for authentication-related API operations
 */
const authService = {
  /**
   * Login user
   * @param {Object} credentials - User credentials
   * @param {string} credentials.email - User email
   * @param {string} credentials.password - User password
   * @returns {Promise<Object>} User data with token
   */
  login: async (credentials) => {
    try {
      const response = await axios.post(AUTH_ENDPOINTS.LOGIN, credentials);

      // Store user data in localStorage
      if (response.data.token) {
        localStorage.setItem("user", JSON.stringify(response.data));
      }

      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Register new user
   * @param {Object} userData - User registration data
   * @returns {Promise<Object>} User data with token
   */
  register: async (userData) => {
    try {
      const response = await axios.post(AUTH_ENDPOINTS.REGISTER, userData);

      // For registration, we don't store user data in localStorage yet
      // User needs to verify email first
      // We'll store it after verification

      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Initiate Google OAuth login
   * This will redirect the user to Google's authentication page
   */
  initiateGoogleLogin: () => {
    window.location.href = AUTH_ENDPOINTS.GOOGLE_LOGIN;
  },

  /**
   * Handle Google OAuth callback
   * This extracts the token from the URL after Google authentication
   * @returns {Object|null} Token data or null if no token found
   */
  handleGoogleCallback: () => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token");

    if (token) {
      // Get existing user data if any
      const existingUser = JSON.parse(localStorage.getItem("user")) || {};

      // Create updated user data with token but preserve onboarding requirement
      const userData = {
        ...existingUser,
        token,
        // DO NOT set onboardingCompleted: true - Google users must also complete onboarding
        // Only preserve existing onboarding status if it exists
        onboardingCompleted: existingUser.onboardingCompleted || false,
      };

      localStorage.setItem("user", JSON.stringify(userData));

      // Remove token from URL
      window.history.replaceState({}, document.title, window.location.pathname);

      return userData;
    }

    return null;
  },

  /**
   * Verify email with verification code
   * @param {Object} verificationData - Email verification data
   * @param {string} verificationData.email - User email
   * @param {string} verificationData.verificationCode - Verification code
   * @returns {Promise<Object>} User data with updated verification status
   */
  verifyEmail: async (verificationData) => {
    try {
      const response = await axios.post(
        AUTH_ENDPOINTS.VERIFY_EMAIL,
        verificationData
      );

      // Update user data in localStorage
      if (response.data.token) {
        localStorage.setItem("user", JSON.stringify(response.data));
      }

      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Resend verification code to email
   * @param {Object} emailData - Email data
   * @param {string} emailData.email - User email
   * @returns {Promise<Object>} Response data
   */
  resendVerificationCode: async (emailData) => {
    try {
      const response = await axios.post(
        AUTH_ENDPOINTS.RESEND_VERIFICATION,
        emailData
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Save onboarding data
   * @param {Object} onboardingData - User onboarding data
   * @returns {Promise<Object>} Updated user data
   */
  saveOnboarding: async (onboardingData) => {
    try {
      // Check if user is authenticated
      const user = JSON.parse(localStorage.getItem("user"));
      if (!user || !user.token) {
        throw new Error("User must be authenticated to complete onboarding");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.post(
        AUTH_ENDPOINTS.ONBOARDING,
        onboardingData
      );

      // Update stored user data
      const updatedUser = {
        ...user,
        ...response.data,
        // Explicitly mark onboarding as completed
        onboardingCompleted: true,
      };
      localStorage.setItem("user", JSON.stringify(updatedUser));

      return updatedUser;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Update user profile
   * @param {Object} profileData - Updated profile data
   * @returns {Promise<Object>} Updated user data
   */
  updateProfile: async (profileData) => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.put(AUTH_ENDPOINTS.PROFILE, profileData);

      // Update stored user data
      const user = JSON.parse(localStorage.getItem("user"));
      const updatedUser = { ...user, ...response.data };
      localStorage.setItem("user", JSON.stringify(updatedUser));

      return updatedUser;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Logout user
   */
  logout: () => {
    localStorage.removeItem("user");
  },

  /**
   * Get current user data from localStorage
   * @returns {Object|null} User data or null if not logged in
   */
  getCurrentUser: () => {
    return JSON.parse(localStorage.getItem("user"));
  },

  /**
   * Refresh JWT token using refresh token
   * @param {string} refreshToken - The refresh token
   * @returns {Promise<Object>} New tokens
   */
  refreshToken: async (refreshToken) => {
    try {
      const response = await axios.post(AUTH_ENDPOINTS.REFRESH_TOKEN, {
        refreshToken,
      });

      // Update user data in localStorage with new tokens
      if (response.data.token) {
        const user = JSON.parse(localStorage.getItem("user"));
        const updatedUser = {
          ...user,
          token: response.data.token,
          refreshToken: response.data.refreshToken || refreshToken,
        };
        localStorage.setItem("user", JSON.stringify(updatedUser));
      }

      return {
        token: response.data.token,
        refreshToken: response.data.refreshToken || refreshToken,
      };
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Request password reset email
   * @param {Object} emailData - Email data
   * @param {string} emailData.email - User email
   * @returns {Promise<Object>} Response data
   */
  forgotPassword: async (emailData) => {
    try {
      const response = await axios.post(
        AUTH_ENDPOINTS.FORGOT_PASSWORD,
        emailData
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Reset password with token
   * @param {string} token - Reset token
   * @param {Object} passwordData - Password data
   * @param {string} passwordData.password - New password
   * @returns {Promise<Object>} Response data
   */
  resetPassword: async (token, passwordData) => {
    try {
      const response = await axios.patch(
        `${AUTH_ENDPOINTS.RESET_PASSWORD}/${token}`,
        passwordData
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Fetch all academic data for onboarding (fields, roles, interests, goals)
   * @returns {Promise<Object>} Object containing all academic data
   */
  fetchAcademicData: async () => {
    try {
      const [fieldsData, rolesData, interestsData, goalsData] = await Promise.all([
        axios.get(AUTH_ENDPOINTS.ACADEMIC_FIELDS),
        axios.get(AUTH_ENDPOINTS.ACADEMIC_ROLES),
        axios.get(AUTH_ENDPOINTS.ACADEMIC_INTERESTS),
        axios.get(AUTH_ENDPOINTS.ACADEMIC_GOALS),
      ]);

      return {
        fields: fieldsData.data,
        roles: rolesData.data,
        interests: interestsData.data,
        goals: goalsData.data,
      };
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Verify institution email and get organization details via ROR API
   * @param {string} email - Institution email to verify
   * @returns {Promise<Object>} Verification result with institution details
   */
  verifyInstitutionEmail: async (email) => {
    try {
      const response = await axios.post(AUTH_ENDPOINTS.VERIFY_INSTITUTION_EMAIL, { email });
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Search institutions by name for manual entry
   * @param {string} query - Search query
   * @returns {Promise<Object>} Search results with institutions list
   */
  searchInstitutions: async (query) => {
    try {
      const response = await axios.get(
        `${AUTH_ENDPOINTS.SEARCH_INSTITUTIONS}?query=${encodeURIComponent(query)}`
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },
};

export default authService;
